import React, { FC, PropsWithChildren, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Divider, Form } from 'antd';
import {
  arraySort,
  canBeEditable,
  momentizeObjectDates,
} from '@core/utils/methods';
import {
  JURISDICTION_DATES_FOR_MOMENTIZE,
  YES_NO_GROUP,
} from '@models/constants';
import { JurisdictionDetailsPermitting } from '@models/interfaces';
import { PermitTypeTable } from '@shared/components';
import {
  Autocomplete,
  DatePicker,
  RadioGroup,
  ShowMoreInput,
} from '@shared/modules';
import { DDVSelectors } from '@store/selectors';
import { PermittingFormProps } from '../../models';

export const PermittingContent: FC<PermittingFormProps> = ({
  isEditing,
  permissions: {
    permittingRequired,
    specialLicensingOrBondingCondition:
      specialLicensingOrBondingConditionPermissions,
    expeditingOrResubmissionApprovalTimeline,
    isWrittenDocumentationFromJurisdiction,
    jurisdictionPermittingNote: jurisdictionPermittingNotePermissions,
    tableAccess,
    jurisdictionReviewDate,
  },
  formValue: {
    permittingTypes,
    id,
    jurisdictionPermittingNote,
    specialLicensingOrBondingCondition,
    ...values
  },
  formUpdating,
}: PropsWithChildren<PermittingFormProps>) => {
  const [form] = Form.useForm();

  const { PermittingRequiredDDV, ExpeditingResubmissionApprovalTimelineDDV } =
    useSelector(
      DDVSelectors.getDDVValuesAsOptionsByTypes([
        'PermittingRequired',
        'ExpeditingResubmissionApprovalTimeline',
      ]),
    );

  useEffect(() => {
    form.setFieldsValue(
      momentizeObjectDates(
        values as JurisdictionDetailsPermitting,
        JURISDICTION_DATES_FOR_MOMENTIZE,
      ),
    );
  }, [isEditing]);

  const update = (): void => {
    if (formUpdating) {
      formUpdating(id, form.getFieldsValue());
    }
  };

  return (
    <>
      <Form
        className="prov-jurisdiction-configurable-panel__form"
        labelCol={{ span: 10 }}
        form={form}
        onValuesChange={update}
      >
        <Autocomplete
          id="permittingRequired"
          className="left"
          label="Permitting Required"
          options={arraySort(PermittingRequiredDDV, 'DESC', 'viewValue')}
          isEditing={canBeEditable(permittingRequired, isEditing)}
          elementProps={{ allowClear: true }}
        />
        <ShowMoreInput
          id="specialLicensingOrBondingCondition"
          className="right"
          label="Special Licensing/Bonding Conditions"
          data={specialLicensingOrBondingCondition}
          form={form}
          isEditing={canBeEditable(
            specialLicensingOrBondingConditionPermissions,
            isEditing,
          )}
        />
        <RadioGroup
          id="isWrittenDocumentationFromJurisdiction"
          className="left"
          label="Written Documentation from Jurisdiction"
          isEditing={canBeEditable(
            isWrittenDocumentationFromJurisdiction,
            isEditing,
          )}
          buttons={YES_NO_GROUP}
        />
        <ShowMoreInput
          id="jurisdictionPermittingNote"
          className="right"
          data={jurisdictionPermittingNote}
          label="Permitting Notes"
          form={form}
          isEditing={canBeEditable(
            jurisdictionPermittingNotePermissions,
            isEditing,
          )}
        />
        <Autocomplete
          id="expeditingOrResubmissionApprovalTimeline"
          className="left"
          label="Expediting/Resubmission Approval Timeline"
          options={ExpeditingResubmissionApprovalTimelineDDV}
          isEditing={canBeEditable(
            expeditingOrResubmissionApprovalTimeline,
            isEditing,
          )}
          elementProps={{ allowClear: true }}
        />
        <DatePicker
          id="jurisdictionReviewDate"
          className="right"
          label="Jurisdiction Review Date"
          isEditing={canBeEditable(jurisdictionReviewDate, isEditing)}
        />
      </Form>
      <Divider />
      <PermitTypeTable
        permittingId={id}
        isEditing={isEditing}
        tableAccess={tableAccess}
        showPermitExpireDate={false}
      />
    </>
  );
};
